import {
    AsYouType,
    getCountries,
    getCountryCallingCode,
    parseIncompletePhoneNumber,
    parsePhoneNumberFromString,
    type CountryCode,
    type MetadataJson,
} from 'libphonenumber-js'
import phoneMetadata from 'libphonenumber-js/metadata.min.json'

import { PHONE_COUNTRY_LOCALSTORAGE_KEY } from '@/constants'
import { COUNTRIES } from '../../../pwa/constants'
import {
    COUNTRY_GROUP_LABELS,
    EUROPEAN_COUNTRIES,
    getCountryFlag,
    normalizeCountryLanguage,
    PRIORITY_COUNTRIES,
    type CountryGroupId,
    type SupportedCountryLanguage,
} from '../country-options'

export type PhoneCountryOption = {
    isoCode: CountryCode
    name: string
    callingCode: string
    searchValue: string
}

export type PhoneCountryGroup = {
    id: CountryGroupId
    label: string
    options: PhoneCountryOption[]
}

export const PRIORITY_PHONE_COUNTRIES = [...PRIORITY_COUNTRIES] as CountryCode[]
const PHONE_COUNTRIES = new Set<CountryCode>(getCountries())
const PHONE_METADATA = phoneMetadata as MetadataJson

export function normalizePhoneLanguage(language: string): SupportedCountryLanguage {
    return normalizeCountryLanguage(language)
}

export function isPhoneCountryCode(value: unknown): value is CountryCode {
    return (
        typeof value === 'string'
        && PHONE_COUNTRIES.has(value.trim().toUpperCase() as CountryCode)
    )
}

export function getPhoneCountryFromValue(value: string): CountryCode | undefined {
    if (!value) {
        return undefined
    }

    return parsePhoneNumberFromString(value)?.country
}

export function getPhoneCountryFromBrowserLanguages(
    languages: readonly string[],
): CountryCode | undefined {
    for (const language of languages) {
        try {
            const region = new Intl.Locale(language).region

            if (isPhoneCountryCode(region)) {
                return region.toUpperCase() as CountryCode
            }
        } catch {
            // Ignore malformed language tags and try the next preference.
        }
    }

    return undefined
}

export function resolveDefaultPhoneCountry({
    phoneValues,
    rememberedCountry,
    billingCountry,
    browserLanguages,
    fallbackCountry = 'CZ',
}: {
    phoneValues: readonly string[]
    rememberedCountry?: string | null
    billingCountry?: string | null
    browserLanguages: readonly string[]
    fallbackCountry?: string | null
}): CountryCode {
    for (const value of phoneValues) {
        const phoneCountry = getPhoneCountryFromValue(value)

        if (phoneCountry) {
            return phoneCountry
        }
    }

    if (isPhoneCountryCode(rememberedCountry)) {
        return rememberedCountry.trim().toUpperCase() as CountryCode
    }

    if (isPhoneCountryCode(billingCountry)) {
        return billingCountry.trim().toUpperCase() as CountryCode
    }

    const browserCountry = getPhoneCountryFromBrowserLanguages(browserLanguages)

    if (browserCountry) {
        return browserCountry
    }

    return isPhoneCountryCode(fallbackCountry)
        ? fallbackCountry.trim().toUpperCase() as CountryCode
        : 'CZ'
}

export function getBrowserLanguages(): string[] {
    if (typeof navigator === 'undefined') {
        return []
    }

    if (navigator.languages?.length > 0) {
        return [...navigator.languages]
    }

    return navigator.language ? [navigator.language] : []
}

export function getStoredPhoneCountry(): CountryCode | null {
    if (typeof window === 'undefined') {
        return null
    }

    try {
        const storedCountry = window.localStorage.getItem(PHONE_COUNTRY_LOCALSTORAGE_KEY)

        return isPhoneCountryCode(storedCountry)
            ? storedCountry.trim().toUpperCase() as CountryCode
            : null
    } catch {
        return null
    }
}

export function setStoredPhoneCountry(country: CountryCode): void {
    if (typeof window === 'undefined' || !isPhoneCountryCode(country)) {
        return
    }

    try {
        window.localStorage.setItem(PHONE_COUNTRY_LOCALSTORAGE_KEY, country)
    } catch {
        return
    }
}

export function getPhoneCountryFlag(countryCode?: string): string {
    return getCountryFlag(countryCode)
}

export function getPhoneCountryOptions(language: string): PhoneCountryOption[] {
    const normalizedLanguage = normalizePhoneLanguage(language)
    const localizedCountries = COUNTRIES[normalizedLanguage]

    return getCountries()
        .map(countryCode => {
            const localizedName = localizedCountries[countryCode]

            if (!localizedName) {
                return null
            }

            const countryName = Array.isArray(localizedName) ? localizedName[0] : localizedName
            const callingCode = getCountryCallingCode(countryCode)

            return {
                isoCode: countryCode,
                name: countryName,
                callingCode: `+${callingCode}`,
                searchValue: `${countryName} ${countryCode} +${callingCode}`.toLowerCase(),
            }
        })
        .filter((option): option is PhoneCountryOption => option !== null)
        .sort((optionA, optionB) => {
            const optionAPriority = PRIORITY_PHONE_COUNTRIES.indexOf(optionA.isoCode)
            const optionBPriority = PRIORITY_PHONE_COUNTRIES.indexOf(optionB.isoCode)

            if (optionAPriority !== -1 || optionBPriority !== -1) {
                if (optionAPriority === -1) {
                    return 1
                }

                if (optionBPriority === -1) {
                    return -1
                }

                return optionAPriority - optionBPriority
            }

            return optionA.name.localeCompare(optionB.name)
        })
}

export function getPhoneCountryGroups(language: string): PhoneCountryGroup[] {
    const normalizedLanguage = normalizePhoneLanguage(language)
    const labels = COUNTRY_GROUP_LABELS[normalizedLanguage]
    const options = getPhoneCountryOptions(normalizedLanguage)

    const frequentOptions = options.filter(option =>
        PRIORITY_PHONE_COUNTRIES.includes(option.isoCode)
    )
    const europeanOptions = options.filter(
        option =>
            !PRIORITY_PHONE_COUNTRIES.includes(option.isoCode) &&
            EUROPEAN_COUNTRIES.has(option.isoCode)
    )
    const worldOptions = options.filter(
        option =>
            !PRIORITY_PHONE_COUNTRIES.includes(option.isoCode) &&
            !EUROPEAN_COUNTRIES.has(option.isoCode)
    )

    const groups: PhoneCountryGroup[] = [
        { id: 'frequent', label: labels.frequent, options: frequentOptions },
        { id: 'europe', label: labels.europe, options: europeanOptions },
        { id: 'world', label: labels.world, options: worldOptions },
    ]

    return groups.filter(group => group.options.length > 0)
}

export function getPhoneCountryFromInput(value: string, fallbackCountry: CountryCode): CountryCode {
    if (!value) {
        return fallbackCountry
    }

    const trimmedValue = value.trim()

    if (!trimmedValue.startsWith('+')) {
        return fallbackCountry
    }

    const formatter = new AsYouType()
    formatter.input(trimmedValue)

    const inferredCountry = formatter.getCountry() ?? getPhoneCountryFromValue(trimmedValue)

    if (inferredCountry) {
        return inferredCountry
    }

    const callingCode = formatter.getCallingCode()
    const fallbackCallingCode = getCountryCallingCode(fallbackCountry)

    if (callingCode === fallbackCallingCode) {
        return fallbackCountry
    }

    const primaryCallingCodeCountry = callingCode
        ? PHONE_METADATA.country_calling_codes[callingCode]?.[0]
        : undefined

    return primaryCallingCodeCountry ?? fallbackCountry
}

export function parsePhoneValue(rawValue: string, defaultCountry: CountryCode) {
    const trimmedValue = rawValue.trim()

    if (trimmedValue === '') {
        return undefined
    }

    return parsePhoneNumberFromString(trimmedValue, {
        defaultCountry,
        extract: false,
    })
}

export function isPossiblePhoneValue(rawValue: string, defaultCountry: CountryCode): boolean {
    return parsePhoneValue(rawValue, defaultCountry)?.isPossible() ?? false
}

export function getNormalizedPhoneValue(rawValue: string, fallbackCountry: CountryCode): string {
    const trimmedValue = rawValue.trim()

    if (trimmedValue === '') {
        return ''
    }

    const parsedPhoneNumber = parsePhoneValue(trimmedValue, fallbackCountry)

    if (parsedPhoneNumber) {
        return parsedPhoneNumber.number
    }

    if (trimmedValue.startsWith('+')) {
        return parseIncompletePhoneNumber(trimmedValue)
    }

    return `+${getCountryCallingCode(fallbackCountry)}${parseIncompletePhoneNumber(trimmedValue)}`
}

export function formatInternationalPhoneValue(value: string): string {
    if (!value) {
        return ''
    }

    const parsedPhoneNumber = parsePhoneNumberFromString(value)

    if (parsedPhoneNumber?.isPossible()) {
        return parsedPhoneNumber.formatInternational()
    }

    return value
}

export function rewritePhoneCountryInInput(
    rawValue: string,
    nextCountry: CountryCode
): string {
    const trimmedValue = rawValue.trim()
    const nextCallingCode = `+${getCountryCallingCode(nextCountry)}`

    if (trimmedValue === '') {
        return `${nextCallingCode} `
    }

    if (trimmedValue.startsWith('+')) {
        const formatter = new AsYouType()
        formatter.input(trimmedValue)

        const normalizedValue = parseIncompletePhoneNumber(trimmedValue)
        const currentCallingCode = formatter.getCallingCode()

        if (currentCallingCode) {
            const remainder = normalizedValue.slice(currentCallingCode.length + 1)
            return remainder ? `${nextCallingCode} ${remainder}` : nextCallingCode
        }

        const digits = normalizedValue.replace(/^\+/, '')
        return digits ? `${nextCallingCode} ${digits}` : nextCallingCode
    }

    const normalizedDigits = parseIncompletePhoneNumber(trimmedValue)
    return normalizedDigits ? `${nextCallingCode} ${normalizedDigits}` : `${nextCallingCode} `
}
